import React, { useState, useContext } from 'react';
import { observer } from 'mobx-react';
import i18nContext from 'components/i18n-context';
import { getErrorMessageForAlert } from 'services/utils';
import Button from 'uikit/Button/Button';
import Alert from 'uikit/Alert/Alert';
import Footer from 'components/common/Footer';
import Loader from 'components/common/Loader';
import FileDisplay from 'components/common/FileDisplay';
import { DOCUMENT_ITEM_TYPES } from 'stores/documentRequestStore';
import SuccessScreen from './SuccessScreen';
import DocumentRequestHeader from './DocumentRequestHeader';
import './styles.scss';

const Confirmation = ({ documentRequestStore, userStore, isAuthorizedFlow, onBack, onEditStep }) => {
  const i18n = useContext(i18nContext);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const logoPath = process.env.REACT_APP_LOGO_PATH;

  // Get document display name
  const getDocumentDisplayName = (item) => {
    if (item.type === DOCUMENT_ITEM_TYPES.OTHER && item.name) {
      return item.name;
    }

    const typeKeys = {
      [DOCUMENT_ITEM_TYPES.INVOICE]: 'documentRequest.types.invoice',
      [DOCUMENT_ITEM_TYPES.AGREEMENT]: 'documentRequest.types.agreement',
      [DOCUMENT_ITEM_TYPES.TRANSPORTATION_DOCUMENTS]: 'documentRequest.types.transportationDocuments',
      [DOCUMENT_ITEM_TYPES.PROOF_OF_WORK]: 'documentRequest.types.proofOfWork',
      [DOCUMENT_ITEM_TYPES.BANK_STATEMENT]: 'documentRequest.types.bankStatement',
      [DOCUMENT_ITEM_TYPES.TAX_DECLARATION]: 'documentRequest.types.taxDeclaration',
      [DOCUMENT_ITEM_TYPES.IDENTIFICATION_DOCUMENT]: 'documentRequest.types.identificationDocument',
      [DOCUMENT_ITEM_TYPES.PROOF_OF_ADDRESS]: 'documentRequest.types.proofOfAddress',
      [DOCUMENT_ITEM_TYPES.PROOF_OF_DIVIDENDS]: 'documentRequest.types.proofOfDividends',
      [DOCUMENT_ITEM_TYPES.PURPOSE_OF_PAYMENT]: 'documentRequest.types.purposeOfPayment',
      [DOCUMENT_ITEM_TYPES.OTHER]: 'documentRequest.types.other'
    };

    return i18n.getMessage(typeKeys[item.type]) || item.type;
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      // Submit the entire document request
      const customerNumber = userStore?.userData?.account?.account_number;
      const response =
        isAuthorizedFlow && customerNumber
          ? await documentRequestStore.submitRequest(customerNumber)
          : await documentRequestStore.submitRequestByToken();

      // Check if status is PROVIDED after submission
      if (response?.status === 'PROVIDED' || documentRequestStore.status === 'PROVIDED') {
        setIsSubmitted(true);
      }
    } catch (error) {
      console.error('Failed to submit document request:', error);

      // If error is because documents already provided, show success screen
      if (error?.code === 'ILLEGAL_STATE' && error?.message?.includes('PROVIDED')) {
        setIsSubmitted(true);
      } else {
        // Show error message for other errors
        const errorMessage = getErrorMessageForAlert(i18n, error, true, null, null);
        setSubmitError(errorMessage || error?.message || 'Failed to submit documents. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return <SuccessScreen />;
  }

  return (
    <div className='document-request-container'>
      <div className='document-request-header'>
        <a href={process.env.REACT_APP_LINK_HOME_PAGE} className='logo'>
          <img src={logoPath} alt='Logo' className='icon-logo' />
        </a>
      </div>

      <div className='document-confirmation-content'>
        <div className='confirmation-header'>
          <h1>{i18n.getMessage('documentRequest.confirmation.reviewTitle')}</h1>
          <p className='subtitle'>{i18n.getMessage('documentRequest.confirmation.reviewSubtitle')}</p>

          {/* Deadline Info Only */}
          <DocumentRequestHeader
            expiresAt={documentRequestStore.expiresAt}
            transactionId={null}
            showDeadline={true}
            showTransaction={false}
          />
        </div>

        {/* Review Section */}
        <div className='review-section'>
          <h2 className='review-title'>{i18n.getMessage('documentRequest.confirmation.reviewTitle')}</h2>
          <p className='review-description'>{i18n.getMessage('documentRequest.confirmation.reviewSubtitle')}</p>
        </div>

        <div className='submitted-documents-section'>
          <div className='documents-list'>
            {documentRequestStore.items &&
              documentRequestStore.items.map((item, index) => (
                <div key={`${item.type}-${index}`} className='document-item submitted'>
                  <div className='document-header'>
                    <div className='document-name'>
                      <span className='document-index-circle'>{index + 1}</span>
                      <span className='document-title'>{getDocumentDisplayName(item)}</span>
                    </div>
                    <button className='edit-button' onClick={() => onEditStep && onEditStep(index + 1)}>
                      {i18n.getMessage('documentRequest.confirmation.edit')}
                    </button>
                  </div>
                  {(item.files?.length > 0 || item.comment) && (
                    <div className='document-content'>
                      <FileDisplay files={item.files || []} comment={item.comment || ''} />
                    </div>
                  )}
                </div>
              ))}
          </div>
        </div>

        {/* Error Alert */}
        {submitError && <Alert type='error' message={submitError} />}

        <div className='action-buttons-confirmation'>
          <Button
            type='outline'
            size='large'
            onClick={onBack}
            isDisabled={isSubmitting}
            // className="back-button"
          >
            {i18n.getMessage('documentRequest.confirmation.back')}
          </Button>
          <Button
            type='primary'
            size='large'
            onClick={handleSubmit}
            isDisabled={isSubmitting}
            className='submit-button'
          >
            {isSubmitting ? <Loader /> : i18n.getMessage('documentRequest.confirmation.submit')}
          </Button>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default observer(Confirmation);
